import React, { useEffect, useRef } from 'react';
import {
  Typography,
  Grid,
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import BorderedSection from '../../shared/borderedSection';
import '../ui/plm.css';

interface PlmEventDetailViewerProps {
  plmEvent?: any | null;
  loading?: boolean;
}

export const PlmEventDetailViewer: React.FC<PlmEventDetailViewerProps> = ({
  plmEvent,
  loading,
}) => {
  const htmlContainerRef = useRef<HTMLDivElement>(null);

  // Handle image loading and errors
  useEffect(() => {
    if (htmlContainerRef.current && plmEvent?.notificationPayload) {
      const cleanedHtml = cleanHtmlContent(plmEvent.notificationPayload);

      console.log('Raw HTML Content:', plmEvent.notificationPayload);
      console.log('Cleaned HTML Content:', cleanedHtml);
      console.log('HTML Content Type:', typeof plmEvent.notificationPayload);
      console.log('HTML Content Length:', plmEvent.notificationPayload?.length);

      // Check if HTML contains img tags
      const hasImgTags = cleanedHtml.includes('<img');
      console.log('Contains <img tags:', hasImgTags);

      // Check the rendered DOM content
      console.log('Rendered HTML:', htmlContainerRef.current.innerHTML);

      const images = htmlContainerRef.current.querySelectorAll('img');
      console.log('Found images:', images.length);

      // Also check for any elements
      const allElements = htmlContainerRef.current.querySelectorAll('*');
      console.log('Total elements found:', allElements.length);
      console.log(
        'Element types:',
        Array.from(allElements).map((el) => el.tagName.toLowerCase()),
      );

      images.forEach((img, index) => {
        console.log(`Image ${index + 1}:`, {
          src: img.src,
          alt: img.alt,
          width: img.width,
          height: img.height,
        });

        // Add error handling for broken images
        img.onerror = () => {
          console.warn('Failed to load image:', img.src);
          img.style.display = 'none'; // Hide broken images
        };

        // Add loading handling
        img.onload = () => {
          console.log('Successfully loaded image:', img.src);
        };

        // Ensure images don't exceed container width
        img.style.maxWidth = '100%';
        img.style.height = 'auto';
      });
    }
  }, [plmEvent?.notificationPayload]);

  if (!plmEvent) {
    return <Typography>No PLM event data available</Typography>;
  }

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatStatus = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const formatNotificationMethod = (method: string | null) => {
    return method ? method.toUpperCase() : 'Not specified';
  };

  // Clean HTML content by replacing \r\n with <br> and unescaping quotes
  const cleanHtmlContent = (html: string) => {
    const cleaned = html
      .replace(/\\r\\n/g, '<br>') // Replace literal \r\n with <br>
      .replace(/\r\n/g, '\n') // Replace actual CRLF
      .replace(/\r/g, '\n') // Replace actual CR
      .replace(/\\"/g, '"') // Replace \" with "
      .replace(/\\'/g, "'"); // Replace \' with '
    return cleaned;
  };

  // Remove <a> tags with 'unsubscribe' in text or href
  const removeUnsubscribeLinks = (html: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    doc.querySelectorAll('a').forEach((a) => {
      if (
        a.textContent?.toLowerCase().includes('unsubscribe') ||
        a.href?.toLowerCase().includes('unsubscribe')
      ) {
        a.remove();
      }
    });
    return doc.body.innerHTML;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        <Box sx={{ marginBottom: '2rem' }}>
          <CircularProgress color="secondary" size="3rem" />
        </Box>
        <Typography variant="h4" color="secondary">
          Loading your data..
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        PLM Event Details
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <BorderedSection title="Basic Information">
            <Grid container spacing={2} className="plm-inner-grid-container">
              <Grid item className="item-wrapper" xs={12} md={6}>
                <List className="plm-ul" dense={false}>
                  <ListItem>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Typography className="label" component="div">
                          Program:
                        </Typography>
                      }
                      secondary={
                        <Typography className="sub-label" component="div">
                          {plmEvent.program?.name || 'N/A'}
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item className="item-wrapper" xs={12} md={6}>
                <List className="plm-ul" dense={false}>
                  <ListItem>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Typography className="label" component="div">
                          Contact:
                        </Typography>
                      }
                      secondary={
                        <Typography className="sub-label" component="div">
                          {plmEvent.contract?.name || 'N/A'}
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item className="item-wrapper" xs={12} md={6}>
                <List className="plm-ul" dense={false}>
                  <ListItem>
                    <ListItemText className="plm-listItem-text">
                      <Typography className="label" component="span">
                        Status:
                      </Typography>
                      <Chip
                        component="div"
                        label={formatStatus(plmEvent.status)}
                        color={
                          plmEvent.status === 'active'
                            ? 'success'
                            : plmEvent.status === 'completed'
                              ? 'primary'
                              : plmEvent.status === 'cancelled'
                                ? 'error'
                                : 'default'
                        }
                        size="small"
                        sx={{
                          borderRadius: '4px',
                          minWidth: '125px',
                          marginLeft: '10px',
                          fontWeight: 'bold',
                        }}
                      />
                    </ListItemText>
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </BorderedSection>
        </Grid>

        <Grid item xs={12} md={6}>
          <BorderedSection title="Timing Information">
            <Grid container spacing={2} className="plm-inner-grid-container">
              <Grid item className="item-wrapper" xs={12} md={6}>
                <List className="plm-ul" dense={false}>
                  <ListItem>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Typography className="label" component="div">
                          Start Time:
                        </Typography>
                      }
                      secondary={
                        <Typography className="sub-label" component="div">
                          {formatDate(plmEvent.startTime)}
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item className="item-wrapper" xs={12} md={6}>
                <List className="plm-ul" dense={false}>
                  <ListItem>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Typography className="label" component="div">
                          Duration:
                        </Typography>
                      }
                      secondary={
                        <Typography className="sub-label" component="div">
                          {plmEvent.duration} minutes
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item className="item-wrapper" xs={12} md={6}>
                <List className="plm-ul" dense={false}>
                  <ListItem>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Typography className="label" component="div">
                          Created At:
                        </Typography>
                      }
                      secondary={
                        <Typography className="sub-label" component="div">
                          {formatDate(plmEvent.createdAt)}
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item className="item-wrapper" xs={12} md={6}>
                <List className="plm-ul" dense={false}>
                  <ListItem>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Typography className="label" component="div">
                          Updated At:
                        </Typography>
                      }
                      secondary={
                        <Typography className="sub-label" component="div">
                          {formatDate(plmEvent.updatedAt)}
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </BorderedSection>
        </Grid>

        <Grid item xs={12}>
          <BorderedSection title="Event Parameters">
            <Grid container spacing={2} className="plm-inner-grid-container">
              <Grid item xs={6} md={3} className="item-wrapper">
                <List className="plm-ul" dense={false}>
                  <ListItem>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Typography className="label" component="div">
                          Reduction Target:
                        </Typography>
                      }
                      secondary={
                        <Typography className="sub-label" component="div">
                          {plmEvent.reductionTarget
                            ? `${plmEvent.reductionTarget} kW`
                            : 'Not specified'}
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </BorderedSection>
        </Grid>

        <Grid item xs={12}>
          <BorderedSection title="Event Notification">
            <Grid container spacing={2} className="plm-inner-grid-container">
              <Grid item xs={12} md={4} className="item-wrapper">
                <List className="plm-ul" dense={false}>
                  <ListItem>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Typography className="label" component="div">
                          Notification Method:
                        </Typography>
                      }
                      secondary={
                        <Typography className="sub-label" component="div">
                          {formatNotificationMethod(
                            plmEvent.notificationMethod,
                          )}
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>

                {/* <Typography variant="subtitle2" color="textSecondary">
                  Notification Method
                </Typography>
                <Typography variant="body1">
                  {formatNotificationMethod(plmEvent.notificationMethod)}
                </Typography> */}
              </Grid>
              <Grid item xs={12} md={4} className="item-wrapper">
                <List className="plm-ul" dense={false}>
                  <ListItem>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Typography className="label" component="div">
                          Notification Received:
                        </Typography>
                      }
                      secondary={
                        <Typography className="sub-label" component="div">
                          {plmEvent.notificationReceivedAt
                            ? formatDate(plmEvent.notificationReceivedAt)
                            : 'Not received'}
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} className="item-wrapper">
                <List className="plm-ul" dense={false}>
                  <ListItem>
                    <ListItemText
                      className="plm-listItem-text"
                      primary={
                        <Typography className="label" component="div">
                          Notification Body
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
                <Box
                  ref={htmlContainerRef}
                  className="plm-html-content"
                  dangerouslySetInnerHTML={{
                    __html:
                      removeUnsubscribeLinks(
                        cleanHtmlContent(plmEvent.notificationPayload),
                      ) || 'No notification body available',
                  }}
                />
              </Grid>
            </Grid>
          </BorderedSection>
        </Grid>
      </Grid>
    </Box>
  );
};
